import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Banknote, Plus, Loader2, KeyRound, UploadCloud, Plug, Store, Trash2, FileDown,
  CheckCircle2, XCircle, Receipt, RefreshCw, Building2, AlertTriangle, Settings2,
  FileMinus, CornerDownRight, X, ChevronDown, ChevronRight, Info,
} from 'lucide-react';
import LotesTab from './ArcaFacturacionLotes';

const CONDICIONES = { ri: 'Responsable Inscripto', monotributo: 'Monotributo', exento: 'Exento / Otro' };
const CBTE_TIPOS = [
  { v: '1', l: '01 - Factura A', cond: 'ri' }, { v: '2', l: '02 - Nota de Débito A', cond: 'ri' }, { v: '3', l: '03 - Nota de Crédito A', cond: 'ri' },
  { v: '6', l: '06 - Factura B', cond: 'ri' }, { v: '7', l: '07 - Nota de Débito B', cond: 'ri' }, { v: '8', l: '08 - Nota de Crédito B', cond: 'ri' },
  { v: '11', l: '11 - Factura C', cond: 'monotributo' }, { v: '12', l: '12 - Nota de Débito C', cond: 'monotributo' }, { v: '13', l: '13 - Nota de Crédito C', cond: 'monotributo' },
];
// Sólo Facturas (para la config por defecto y el select de emisión "normal")
const FACTURA_TIPO_OPTS = CBTE_TIPOS.filter((t) => ['1', '6', '11'].includes(t.v));
const CONCEPTOS = [{ v: '1', l: 'Productos' }, { v: '2', l: 'Servicios' }, { v: '3', l: 'Productos y Servicios' }];
const DOC_TIPOS = [{ v: '80', l: '80 - CUIT' }, { v: '86', l: '86 - CUIL' }, { v: '96', l: '96 - DNI' }, { v: '99', l: '99 - Consumidor Final' }];
const COND_IVA_RECEPTOR = [
  { v: '1', l: 'IVA Responsable Inscripto' }, { v: '6', l: 'Responsable Monotributo' }, { v: '4', l: 'IVA Sujeto Exento' },
  { v: '5', l: 'Consumidor Final' }, { v: '13', l: 'Monotributista Social' }, { v: '7', l: 'Sujeto No Categorizado' },
  { v: '8', l: 'Proveedor del Exterior' }, { v: '9', l: 'Cliente del Exterior' }, { v: '15', l: 'IVA No Alcanzado' },
];
const ALICUOTAS = [{ v: '21', l: '21%' }, { v: '10.5', l: '10,5%' }, { v: '27', l: '27%' }, { v: '0', l: '0%' }];

const CBTE_LABEL = { 1: 'Factura A', 2: 'N. Débito A', 3: 'N. Crédito A', 6: 'Factura B', 7: 'N. Débito B', 8: 'N. Crédito B', 11: 'Factura C', 12: 'N. Débito C', 13: 'N. Crédito C' };
const cbteLabel = (t) => CBTE_LABEL[Number(t)] || `Tipo ${t}`;
const FACTURA_TIPOS = new Set([1, 6, 11]);
const NC_TIPOS = new Set([3, 8, 13]);
const ND_TIPOS = new Set([2, 7, 12]);
const NC_DE = { 1: 3, 6: 8, 11: 13 }; // factura -> nota de crédito correspondiente
const esNcTipo = (t) => NC_TIPOS.has(Number(t)) || ND_TIPOS.has(Number(t));

const money = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : n.toLocaleString('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 });
};
const fmt = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString('es-AR'); } catch { return iso; } };
const hoyYmd = () => { const d = new Date(); return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`; };
const cbteNroFmt = (c) => `${String(c.pto_vta).padStart(4, '0')}-${String(c.cbte_nro).padStart(8, '0')}`;

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-800 text-right break-all">{value ?? '—'}</span>
  </div>
);

// ======================= Empresas =======================
const EmpresasTab = ({ emisores, reload }) => {
  const [openAdd, setOpenAdd] = useState(false);
  const [nuevo, setNuevo] = useState({ cuit: '', razon_social: '', condicion: 'ri', environment: 'homologacion' });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState({});
  const [csrModal, setCsrModal] = useState(null);
  const [certModal, setCertModal] = useState(null);
  const [certForm, setCertForm] = useState({ cert_pem: '', key_pem: '' });
  const [cfgModal, setCfgModal] = useState(null);
  const [cfg, setCfg] = useState({});
  const [cfgSaving, setCfgSaving] = useState(false);

  const setB = (id, v) => setBusy((p) => ({ ...p, [id]: v }));

  const crear = async () => {
    if (nuevo.cuit.replace(/\D/g, '').length !== 11) { toast.error('CUIT inválido'); return; }
    if (!nuevo.razon_social.trim()) { toast.error('Razón social requerida'); return; }
    setSaving(true);
    try {
      await api.post('/arca/facturacion/emisores', { ...nuevo, cuit: nuevo.cuit.replace(/\D/g, '') });
      toast.success('Empresa agregada');
      setOpenAdd(false); setNuevo({ cuit: '', razon_social: '', condicion: 'ri', environment: 'homologacion' });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };

  const generarCsr = async (em) => {
    setB(em.id, 'csr');
    try {
      const res = await api.post(`/arca/facturacion/emisores/${em.id}/csr`);
      setCsrModal({ emisor: em, csr: res.data.csr_pem });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error generando CSR'); } finally { setB(em.id, null); }
  };

  const descargarCsr = (csr, cuit) => {
    const blob = new Blob([csr], { type: 'application/x-pem-file' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `pedido_${cuit}.csr`; a.click();
    URL.revokeObjectURL(url);
  };

  const subirCert = async () => {
    if (!certForm.cert_pem.trim()) { toast.error('Pegá el certificado (.crt)'); return; }
    setB(certModal.id, 'cert');
    try {
      await api.post(`/arca/facturacion/emisores/${certModal.id}/cert`, {
        cert_pem: certForm.cert_pem, key_pem: certForm.key_pem || null,
      });
      toast.success('Certificado cargado');
      setCertModal(null); setCertForm({ cert_pem: '', key_pem: '' }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setB(certModal?.id, null); }
  };

  const probar = async (em) => {
    setB(em.id, 'test');
    try {
      const res = await api.post(`/arca/facturacion/emisores/${em.id}/test`);
      if (res.data.success) toast.success(res.data.message || 'Conexión OK');
      else toast.error(res.data.error || 'Falló la conexión');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setB(em.id, null); }
  };

  const traerPV = async (em) => {
    setB(em.id, 'pv');
    try {
      const res = await api.get(`/arca/facturacion/emisores/${em.id}/puntos-venta`);
      const pvs = res.data.puntos_venta || [];
      if (pvs.length) toast.success(`Puntos de venta: ${pvs.map((p) => p.nro).join(', ')}`);
      else toast.info('No se encontraron puntos de venta habilitados para WS');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setB(em.id, null); }
  };

  const cambiarAmbiente = async (em, environment) => {
    try { await api.put(`/arca/facturacion/emisores/${em.id}`, { environment }); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const borrar = async (em) => {
    if (!window.confirm(`¿Eliminar la empresa ${em.razon_social}? Se borrará su certificado.`)) return;
    try { await api.delete(`/arca/facturacion/emisores/${em.id}`); toast.success('Empresa eliminada'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const abrirCfg = (em) => {
    const dc = em.default_config || {};
    setCfg({
      pto_vta: dc.pto_vta != null ? String(dc.pto_vta) : '',
      cbte_tipo: dc.cbte_tipo != null ? String(dc.cbte_tipo) : (em.condicion === 'monotributo' ? '11' : '6'),
      concepto: dc.concepto != null ? String(dc.concepto) : '1',
      doc_tipo: dc.doc_tipo != null ? String(dc.doc_tipo) : '99',
      doc_nro: dc.doc_nro != null ? String(dc.doc_nro) : '0',
      condicion_iva_receptor_id: dc.condicion_iva_receptor_id != null ? String(dc.condicion_iva_receptor_id) : '5',
      iva_alicuota: dc.iva_alicuota || '21',
      iva_incluido: dc.iva_incluido !== undefined ? !!dc.iva_incluido : true,
    });
    setCfgModal(em);
  };

  const guardarCfg = async () => {
    setCfgSaving(true);
    try {
      await api.put(`/arca/facturacion/emisores/${cfgModal.id}`, { default_config: cfg });
      toast.success('Configuración por defecto guardada');
      setCfgModal(null); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setCfgSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setOpenAdd(true)} data-testid="fact-add-empresa-btn" className="bg-slate-800 hover:bg-slate-900">
          <Plus size={16} className="mr-2" /> Agregar empresa
        </Button>
      </div>

      {emisores.length === 0 ? (
        <Card className="p-8 text-center text-slate-400 text-sm">Todavía no hay empresas cargadas. Agregá la primera para empezar.</Card>
      ) : emisores.map((em) => (
        <Card key={em.id} className="p-5" data-testid={`fact-empresa-${em.id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <Building2 size={18} className="text-slate-500" />
                <h3 className="font-semibold text-slate-900">{em.razon_social}</h3>
                <Badge variant="outline">{CONDICIONES[em.condicion] || em.condicion}</Badge>
              </div>
              <p className="font-mono text-sm text-slate-500 mt-1">CUIT {em.cuit}</p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge className={em.has_cert ? 'bg-emerald-600' : em.has_csr ? 'bg-amber-500' : 'bg-slate-400'}>
                  {em.has_cert ? 'Certificado cargado' : em.has_csr ? 'CSR generado (falta .crt)' : 'Sin certificado'}
                </Badge>
                {em.cert_info?.not_after && <span className="text-xs text-slate-400">vence {fmt(em.cert_info.not_after)}</span>}
                {em.default_config?.pto_vta != null && (
                  <Badge variant="outline" className="text-xs" data-testid={`fact-cfg-badge-${em.id}`}>
                    Default: PV {em.default_config.pto_vta} · {cbteLabel(em.default_config.cbte_tipo)}{em.default_config.iva_incluido ? ' · IVA incl.' : ''}
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={em.environment} onValueChange={(v) => cambiarAmbiente(em, v)}>
                <SelectTrigger className="w-40 h-9" data-testid={`fact-env-${em.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="homologacion">Homologación</SelectItem>
                  <SelectItem value="produccion">Producción</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="ghost" size="icon" onClick={() => borrar(em)} data-testid={`fact-del-${em.id}`}><Trash2 size={16} className="text-rose-500" /></Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={() => abrirCfg(em)} data-testid={`fact-cfg-${em.id}`}>
              <Settings2 size={14} className="mr-1" /> Config por defecto
            </Button>
            <Button variant="outline" size="sm" disabled={busy[em.id]} onClick={() => generarCsr(em)} data-testid={`fact-csr-${em.id}`}>
              {busy[em.id] === 'csr' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <KeyRound size={14} className="mr-1" />} Generar CSR
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setCertModal(em); setCertForm({ cert_pem: '', key_pem: '' }); }} data-testid={`fact-uploadcert-${em.id}`}>
              <UploadCloud size={14} className="mr-1" /> Subir certificado
            </Button>
            <Button variant="outline" size="sm" disabled={busy[em.id] || !em.has_cert} onClick={() => probar(em)} data-testid={`fact-test-${em.id}`}>
              {busy[em.id] === 'test' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Plug size={14} className="mr-1" />} Probar conexión
            </Button>
            <Button variant="outline" size="sm" disabled={busy[em.id] || !em.has_cert} onClick={() => traerPV(em)} data-testid={`fact-pv-${em.id}`}>
              {busy[em.id] === 'pv' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Store size={14} className="mr-1" />} Traer puntos de venta
            </Button>
          </div>
        </Card>
      ))}

      {/* Alta empresa */}
      <Dialog open={openAdd} onOpenChange={setOpenAdd}>
        <DialogContent data-testid="fact-add-dialog">
          <DialogHeader><DialogTitle>Agregar empresa emisora</DialogTitle><DialogDescription>Cargá los datos fiscales de la empresa que va a emitir comprobantes.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>CUIT</Label><Input value={nuevo.cuit} onChange={(e) => setNuevo({ ...nuevo, cuit: e.target.value })} className="font-mono mt-1" placeholder="30123456789" data-testid="fact-new-cuit" /></div>
            <div><Label>Razón social</Label><Input value={nuevo.razon_social} onChange={(e) => setNuevo({ ...nuevo, razon_social: e.target.value })} className="mt-1" placeholder="EMPRESA S.A." data-testid="fact-new-razon" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Condición</Label>
                <Select value={nuevo.condicion} onValueChange={(v) => setNuevo({ ...nuevo, condicion: v })}>
                  <SelectTrigger className="mt-1" data-testid="fact-new-cond"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CONDICIONES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Ambiente</Label>
                <Select value={nuevo.environment} onValueChange={(v) => setNuevo({ ...nuevo, environment: v })}>
                  <SelectTrigger className="mt-1" data-testid="fact-new-env"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="homologacion">Homologación</SelectItem><SelectItem value="produccion">Producción</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={crear} disabled={saving} data-testid="fact-save-empresa">{saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : null} Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config por defecto */}
      <Dialog open={!!cfgModal} onOpenChange={(o) => !o && setCfgModal(null)}>
        <DialogContent className="max-w-lg" data-testid="fact-cfg-dialog">
          <DialogHeader>
            <DialogTitle>Configuración por defecto — {cfgModal?.razon_social}</DialogTitle>
            <DialogDescription>Estos valores se cargan automáticamente al emitir con esta empresa. Igual podés cambiarlos en cada factura.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Punto de venta</Label><Input value={cfg.pto_vta} onChange={(e) => setCfg({ ...cfg, pto_vta: e.target.value.replace(/\D/g, '') })} className="font-mono mt-1" placeholder="1" data-testid="fact-cfg-ptovta" /></div>
            <div><Label>Tipo de comprobante</Label>
              <Select value={cfg.cbte_tipo} onValueChange={(v) => setCfg({ ...cfg, cbte_tipo: v })}>
                <SelectTrigger className="mt-1" data-testid="fact-cfg-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{FACTURA_TIPO_OPTS.filter((t) => !cfgModal || t.cond === cfgModal.condicion).map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Doc. receptor</Label>
              <Select value={cfg.doc_tipo} onValueChange={(v) => setCfg({ ...cfg, doc_tipo: v })}>
                <SelectTrigger className="mt-1" data-testid="fact-cfg-doctipo"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_TIPOS.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Nº documento</Label><Input value={cfg.doc_nro} onChange={(e) => setCfg({ ...cfg, doc_nro: e.target.value.replace(/\D/g, '') })} className="font-mono mt-1" placeholder="0" data-testid="fact-cfg-docnro" /></div>
            <div><Label>Condición IVA receptor</Label>
              <Select value={cfg.condicion_iva_receptor_id} onValueChange={(v) => setCfg({ ...cfg, condicion_iva_receptor_id: v })}>
                <SelectTrigger className="mt-1" data-testid="fact-cfg-condreceptor"><SelectValue /></SelectTrigger>
                <SelectContent>{COND_IVA_RECEPTOR.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Alícuota IVA</Label>
              <Select value={cfg.iva_alicuota} onValueChange={(v) => setCfg({ ...cfg, iva_alicuota: v })}>
                <SelectTrigger className="mt-1" data-testid="fact-cfg-alic"><SelectValue /></SelectTrigger>
                <SelectContent>{ALICUOTAS.map((a) => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3 mt-1">
            <div>
              <p className="font-medium text-sm text-slate-800">Importe con IVA incluido</p>
              <p className="text-xs text-slate-500">Cargás el total final y el sistema calcula el neto y el IVA.</p>
            </div>
            <Switch checked={!!cfg.iva_incluido} onCheckedChange={(v) => setCfg({ ...cfg, iva_incluido: v })} data-testid="fact-cfg-ivaincl" />
          </div>
          <DialogFooter>
            <Button onClick={guardarCfg} disabled={cfgSaving} data-testid="fact-cfg-save">{cfgSaving ? <Loader2 size={16} className="mr-2 animate-spin" /> : null} Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSR generado */}
      <Dialog open={!!csrModal} onOpenChange={(o) => !o && setCsrModal(null)}>
        <DialogContent className="max-w-2xl" data-testid="fact-csr-dialog">
          <DialogHeader><DialogTitle>CSR de {csrModal?.emisor?.razon_social}</DialogTitle><DialogDescription>Descargá o copiá este CSR y subilo a ARCA (Administración de Certificados Digitales) para obtener el .crt.</DialogDescription></DialogHeader>
          <Textarea readOnly value={csrModal?.csr || ''} className="font-mono text-xs h-48" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(csrModal.csr); toast.success('CSR copiado'); }}>Copiar</Button>
            <Button onClick={() => descargarCsr(csrModal.csr, csrModal.emisor.cuit)}><FileDown size={16} className="mr-2" /> Descargar .csr</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subir cert */}
      <Dialog open={!!certModal} onOpenChange={(o) => !o && setCertModal(null)}>
        <DialogContent className="max-w-2xl" data-testid="fact-cert-dialog">
          <DialogHeader><DialogTitle>Subir certificado — {certModal?.razon_social}</DialogTitle><DialogDescription>Pegá el certificado que emite ARCA. Si generaste la clave con OpenSSL, pegá también la clave privada.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Certificado (.crt / .pem que emite ARCA)</Label>
              <Textarea value={certForm.cert_pem} onChange={(e) => setCertForm({ ...certForm, cert_pem: e.target.value })} className="font-mono text-xs h-32 mt-1" placeholder="-----BEGIN CERTIFICATE-----" data-testid="fact-cert-input" /></div>
            <div><Label>Clave privada (opcional — sólo si generaste el CSR por fuera con OpenSSL)</Label>
              <Textarea value={certForm.key_pem} onChange={(e) => setCertForm({ ...certForm, key_pem: e.target.value })} className="font-mono text-xs h-24 mt-1" placeholder="-----BEGIN RSA PRIVATE KEY----- (dejar vacío si generaste el CSR en la app)" data-testid="fact-key-input" /></div>
          </div>
          <DialogFooter>
            <Button onClick={subirCert} disabled={busy[certModal?.id] === 'cert'} data-testid="fact-cert-save">
              {busy[certModal?.id] === 'cert' ? <Loader2 size={16} className="mr-2 animate-spin" /> : <UploadCloud size={16} className="mr-2" />} Cargar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ======================= Emitir =======================
const defaultForm = () => ({
  pto_vta: '', cbte_tipo: '', concepto: '1', doc_tipo: '99', doc_nro: '0',
  condicion_iva_receptor_id: '5', cbte_fch: '', imp_neto: '', iva_alicuota: '21', imp_total: '',
  fch_serv_desde: '', fch_serv_hasta: '', fch_vto_pago: '',
});

const EmitirTab = ({ emisores, onEmitido, preset, clearPreset }) => {
  const listos = emisores.filter((e) => e.has_cert);
  const [emisorId, setEmisorId] = useState('');
  const emisor = emisores.find((e) => e.id === emisorId);
  const [f, setF] = useState(defaultForm());
  const [ivaIncl, setIvaIncl] = useState(true);
  const [nc, setNc] = useState(null); // { id, origen }
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const aplicarDefault = useCallback((id) => {
    const dc = emisores.find((e) => e.id === id)?.default_config || {};
    setF({
      ...defaultForm(),
      pto_vta: dc.pto_vta != null ? String(dc.pto_vta) : '',
      cbte_tipo: dc.cbte_tipo != null ? String(dc.cbte_tipo) : '',
      concepto: dc.concepto != null ? String(dc.concepto) : '1',
      doc_tipo: dc.doc_tipo != null ? String(dc.doc_tipo) : '99',
      doc_nro: dc.doc_nro != null ? String(dc.doc_nro) : '0',
      condicion_iva_receptor_id: dc.condicion_iva_receptor_id != null ? String(dc.condicion_iva_receptor_id) : '5',
      iva_alicuota: dc.iva_alicuota || '21',
    });
    setIvaIncl(dc.iva_incluido !== undefined ? !!dc.iva_incluido : true);
  }, [emisores]);

  // Preset (Nota de crédito desde una factura)
  useEffect(() => {
    if (!preset) return;
    setEmisorId(preset.emisor_id);
    setNc({ id: preset.nc_de_comprobante_id, origen: preset.origen });
    setResult(null); setError('');
    setIvaIncl(true);
    setF({
      ...defaultForm(),
      pto_vta: preset.pto_vta || '', cbte_tipo: preset.cbte_tipo || '', concepto: '1',
      doc_tipo: preset.doc_tipo || '99', doc_nro: preset.doc_nro || '0',
      condicion_iva_receptor_id: preset.condicion_iva_receptor_id || '5',
      iva_alicuota: preset.iva_alicuota || '21', imp_total: preset.imp_total || '',
    });
  }, [preset]);

  const pickEmisor = (id) => { setEmisorId(id); setResult(null); setError(''); setNc(null); if (clearPreset) clearPreset(); aplicarDefault(id); };
  const cancelarNc = () => { setNc(null); if (clearPreset) clearPreset(); aplicarDefault(emisorId); };

  const esC = ['11', '12', '13'].includes(f.cbte_tipo);
  const usaTotal = esC || ivaIncl; // se carga el total (con IVA)
  const alic = parseFloat(f.iva_alicuota || 0) || 0;
  const totalInput = parseFloat(f.imp_total || 0) || 0;
  const netoInput = parseFloat(f.imp_neto || 0) || 0;
  let netoCalc, ivaCalc, totalCalc;
  if (esC) { totalCalc = totalInput; netoCalc = totalInput; ivaCalc = 0; }
  else if (ivaIncl) { totalCalc = totalInput; netoCalc = +(totalInput / (1 + alic / 100)).toFixed(2); ivaCalc = +(totalCalc - netoCalc).toFixed(2); }
  else { netoCalc = netoInput; ivaCalc = +(netoInput * alic / 100).toFixed(2); totalCalc = +(netoInput + ivaCalc).toFixed(2); }

  const tiposFiltrados = nc
    ? CBTE_TIPOS.filter((t) => [NC_DE[Number(preset?.origen?.tipo)], Number(preset?.origen?.tipo)].includes(Number(t.v)) || (!emisor || t.cond === emisor.condicion))
    : CBTE_TIPOS.filter((t) => !emisor || t.cond === emisor.condicion);

  const emitir = async () => {
    if (!emisorId) { toast.error('Elegí una empresa'); return; }
    if (!f.pto_vta || !f.cbte_tipo) { toast.error('Completá punto de venta y tipo de comprobante'); return; }
    if (usaTotal && totalCalc <= 0) { toast.error('Ingresá el importe total'); return; }
    if (!usaTotal && netoCalc <= 0) { toast.error('Ingresá el importe neto'); return; }
    setLoading(true); setError(''); setResult(null);
    try {
      const body = {
        pto_vta: Number(f.pto_vta), cbte_tipo: Number(f.cbte_tipo), concepto: Number(f.concepto),
        doc_tipo: Number(f.doc_tipo), doc_nro: Number((f.doc_nro || '0').replace(/\D/g, '') || 0),
        condicion_iva_receptor_id: Number(f.condicion_iva_receptor_id),
        cbte_fch: f.cbte_fch ? f.cbte_fch.replaceAll('-', '') : hoyYmd(),
        iva_alicuota: f.iva_alicuota, iva_incluido: !esC && ivaIncl,
        imp_neto: esC ? totalCalc : netoCalc, imp_iva: ivaCalc, imp_total: totalCalc,
      };
      if (Number(f.concepto) !== 1) {
        body.fch_serv_desde = (f.fch_serv_desde || '').replaceAll('-', '') || hoyYmd();
        body.fch_serv_hasta = (f.fch_serv_hasta || '').replaceAll('-', '') || hoyYmd();
        body.fch_vto_pago = (f.fch_vto_pago || '').replaceAll('-', '') || hoyYmd();
      }
      if (nc) {
        body.nc_de_comprobante_id = nc.id;
        body.cbtes_asoc = [{ tipo: Number(nc.origen.tipo), pto_vta: Number(nc.origen.pto_vta), nro: Number(nc.origen.nro), cbte_fch: nc.origen.cbte_fch || null }];
      }
      const res = await api.post(`/arca/facturacion/emisores/${emisorId}/emitir`, body);
      setResult(res.data);
      if (res.data.ok) {
        toast.success(`CAE ${res.data.cae}`);
        if (nc) { setNc(null); if (clearPreset) clearPreset(); }
        onEmitido && onEmitido();
      } else toast.error('El comprobante no fue autorizado');
    } catch (e) { setError(e.response?.data?.detail || 'Error emitiendo'); } finally { setLoading(false); }
  };

  if (listos.length === 0) {
    return <Card className="p-8 text-center text-slate-400 text-sm">Para emitir necesitás al menos una empresa con certificado cargado (pestaña Empresas).</Card>;
  }

  return (
    <div className="space-y-5">
      {nc && (
        <Card className="p-4 border-indigo-200 bg-indigo-50 flex items-center justify-between gap-4" data-testid="fact-nc-banner">
          <div className="flex items-center gap-3 text-indigo-800">
            <FileMinus size={18} />
            <div>
              <p className="font-semibold">Nota de Crédito de {nc.origen.label}</p>
              <p className="text-sm text-indigo-600/80">Se emite asociada al comprobante original. Podés ajustar el importe si es una NC parcial.</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={cancelarNc} data-testid="fact-nc-cancel"><X size={15} className="mr-1" /> Cancelar NC</Button>
        </Card>
      )}

      <Card className="p-5 space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div><Label>Empresa emisora</Label>
            <Select value={emisorId} onValueChange={pickEmisor} disabled={!!nc}>
              <SelectTrigger className="mt-1" data-testid="fact-emitir-empresa"><SelectValue placeholder="Elegí…" /></SelectTrigger>
              <SelectContent>{listos.map((e) => <SelectItem key={e.id} value={e.id}>{e.razon_social} · {e.environment === 'produccion' ? 'PROD' : 'HOMO'}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Punto de venta</Label><Input value={f.pto_vta} onChange={(e) => set('pto_vta', e.target.value.replace(/\D/g, ''))} className="font-mono mt-1" placeholder="1" data-testid="fact-emitir-ptovta" /></div>
          <div><Label>Tipo de comprobante</Label>
            <Select value={f.cbte_tipo} onValueChange={(v) => set('cbte_tipo', v)}>
              <SelectTrigger className="mt-1" data-testid="fact-emitir-tipo"><SelectValue placeholder="Elegí…" /></SelectTrigger>
              <SelectContent>{tiposFiltrados.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Concepto</Label>
            <Select value={f.concepto} onValueChange={(v) => set('concepto', v)}>
              <SelectTrigger className="mt-1" data-testid="fact-emitir-concepto"><SelectValue /></SelectTrigger>
              <SelectContent>{CONCEPTOS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Fecha comprobante</Label><Input type="date" value={f.cbte_fch} onChange={(e) => set('cbte_fch', e.target.value)} className="mt-1" data-testid="fact-emitir-fecha" /><p className="text-xs text-slate-400 mt-1">Vacío = hoy</p></div>
          <div />
          <div><Label>Doc. receptor</Label>
            <Select value={f.doc_tipo} onValueChange={(v) => set('doc_tipo', v)}>
              <SelectTrigger className="mt-1" data-testid="fact-emitir-doctipo"><SelectValue /></SelectTrigger>
              <SelectContent>{DOC_TIPOS.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Nº documento receptor</Label><Input value={f.doc_nro} onChange={(e) => set('doc_nro', e.target.value.replace(/\D/g, ''))} className="font-mono mt-1" placeholder="0 para Consumidor Final" data-testid="fact-emitir-docnro" /></div>
          <div><Label>Condición IVA receptor</Label>
            <Select value={f.condicion_iva_receptor_id} onValueChange={(v) => set('condicion_iva_receptor_id', v)}>
              <SelectTrigger className="mt-1" data-testid="fact-emitir-condreceptor"><SelectValue /></SelectTrigger>
              <SelectContent>{COND_IVA_RECEPTOR.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {Number(f.concepto) !== 1 && (
          <div className="grid md:grid-cols-3 gap-4">
            <div><Label>Serv. desde</Label><Input type="date" value={f.fch_serv_desde} onChange={(e) => set('fch_serv_desde', e.target.value)} className="mt-1" /></div>
            <div><Label>Serv. hasta</Label><Input type="date" value={f.fch_serv_hasta} onChange={(e) => set('fch_serv_hasta', e.target.value)} className="mt-1" /></div>
            <div><Label>Vto. pago</Label><Input type="date" value={f.fch_vto_pago} onChange={(e) => set('fch_vto_pago', e.target.value)} className="mt-1" /></div>
          </div>
        )}

        {/* Importe */}
        <div className="border-t pt-4 space-y-3">
          {!esC && (
            <div className="flex items-center justify-between rounded-lg bg-slate-50 border p-3">
              <div>
                <p className="font-medium text-sm text-slate-800">Importe con IVA incluido</p>
                <p className="text-xs text-slate-500">{ivaIncl ? 'Cargás el total final y calculamos el neto y el IVA.' : 'Cargás el neto y sumamos el IVA por encima.'}</p>
              </div>
              <Switch checked={ivaIncl} onCheckedChange={setIvaIncl} data-testid="fact-emitir-ivaincl" />
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-4 items-end">
            {esC ? (
              <div><Label>Importe total (Factura C)</Label><Input type="number" step="0.01" value={f.imp_total} onChange={(e) => set('imp_total', e.target.value)} className="font-mono mt-1" placeholder="12100.00" data-testid="fact-emitir-total" /></div>
            ) : ivaIncl ? (
              <>
                <div><Label>Importe total (IVA incluido)</Label><Input type="number" step="0.01" value={f.imp_total} onChange={(e) => set('imp_total', e.target.value)} className="font-mono mt-1" placeholder="121.00" data-testid="fact-emitir-total" /></div>
                <div><Label>Alícuota IVA</Label>
                  <Select value={f.iva_alicuota} onValueChange={(v) => set('iva_alicuota', v)}>
                    <SelectTrigger className="mt-1" data-testid="fact-emitir-alic"><SelectValue /></SelectTrigger>
                    <SelectContent>{ALICUOTAS.map((a) => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <>
                <div><Label>Importe neto</Label><Input type="number" step="0.01" value={f.imp_neto} onChange={(e) => set('imp_neto', e.target.value)} className="font-mono mt-1" placeholder="100.00" data-testid="fact-emitir-neto" /></div>
                <div><Label>Alícuota IVA</Label>
                  <Select value={f.iva_alicuota} onValueChange={(v) => set('iva_alicuota', v)}>
                    <SelectTrigger className="mt-1" data-testid="fact-emitir-alic"><SelectValue /></SelectTrigger>
                    <SelectContent>{ALICUOTAS.map((a) => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div className="text-sm text-slate-600" data-testid="fact-emitir-preview">
              {!esC && <div>Neto: <b className="font-mono">{money(netoCalc)}</b></div>}
              {!esC && <div>IVA: <b className="font-mono">{money(ivaCalc)}</b></div>}
              <div className="text-base">Total: <b className="text-slate-900 font-mono">{money(totalCalc)}</b></div>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={emitir} disabled={loading} data-testid="fact-emitir-btn" className={nc ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-emerald-600 hover:bg-emerald-700'}>
            {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : (nc ? <FileMinus size={16} className="mr-2" /> : <Receipt size={16} className="mr-2" />)}
            {loading ? 'Solicitando CAE…' : (nc ? 'Emitir Nota de Crédito' : 'Emitir (pedir CAE)')}
          </Button>
        </div>
      </Card>

      {error && (
        <Card className="p-5 border-amber-200 bg-amber-50" data-testid="fact-emitir-error">
          <div className="flex items-start gap-3 text-amber-800"><AlertTriangle size={18} className="mt-0.5" /><p className="font-medium">{error}</p></div>
        </Card>
      )}

      {result && (
        <Card className="p-6" data-testid="fact-emitir-result">
          <div className={`flex items-center gap-3 p-4 rounded-lg ${result.ok ? 'bg-emerald-50 border border-emerald-200' : 'bg-rose-50 border border-rose-200'}`}>
            {result.ok ? <CheckCircle2 size={26} className="text-emerald-600" /> : <XCircle size={26} className="text-rose-600" />}
            <div>
              <p className={`font-semibold ${result.ok ? 'text-emerald-800' : 'text-rose-800'}`}>
                {result.ok ? `Comprobante AUTORIZADO — CAE ${result.cae}` : `No autorizado (${result.resultado || 'error'})`}
              </p>
              <p className="text-sm text-slate-500">Nº {String(result.pto_vta).padStart(4, '0')}-{String(result.cbte_nro).padStart(8, '0')} · Vto CAE: {result.cae_vto || '—'}</p>
            </div>
          </div>
          {result.ok && result.comprobante_id && (
            <Button variant="outline" size="sm" className="mt-3" data-testid="fact-emitir-pdf" onClick={async () => {
              try {
                const res = await api.get(`/arca/facturacion/comprobantes/${result.comprobante_id}/pdf`, { responseType: 'blob' });
                const url = URL.createObjectURL(res.data); window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60000);
              } catch (e) { toast.error('No se pudo generar el PDF'); }
            }}><FileDown size={14} className="mr-1" /> Descargar PDF</Button>
          )}
          {Array.isArray(result.observaciones) && result.observaciones.length > 0 && (
            <ul className="mt-3 text-sm text-amber-700 list-disc pl-5">{result.observaciones.map((o, i) => <li key={i}>[{o.code}] {o.msg}</li>)}</ul>
          )}
          {Array.isArray(result.errores) && result.errores.length > 0 && (
            <ul className="mt-3 text-sm text-rose-700 list-disc pl-5">{result.errores.map((o, i) => <li key={i}>[{o.code}] {o.msg}</li>)}</ul>
          )}
        </Card>
      )}
    </div>
  );
};

// ======================= Comprobantes =======================
const EstadoBadge = ({ c }) => (
  String(c.resultado).toUpperCase() === 'A'
    ? <Badge className="bg-emerald-600" data-testid={`fact-estado-${c.id}`}>CAE {c.cae}</Badge>
    : <Badge className="bg-rose-600" data-testid={`fact-estado-${c.id}`}>Rechazado</Badge>
);

const ComprobantesTab = ({ emisores, refreshKey, onNotaCredito }) => {
  const [filtro, setFiltro] = useState('all');
  const [estado, setEstado] = useState('all');
  const [tipoF, setTipoF] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detalle, setDetalle] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtro !== 'all') params.emisor_id = filtro;
      const res = await api.get('/arca/facturacion/comprobantes', { params });
      setRows(res.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setLoading(false); }
  }, [filtro]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const byId = React.useMemo(() => Object.fromEntries(rows.map((r) => [r.id, r])), [rows]);
  const ncByFact = React.useMemo(() => {
    const m = {};
    rows.forEach((r) => { if (r.nc_de_comprobante_id && esNcTipo(r.cbte_tipo)) { (m[r.nc_de_comprobante_id] = m[r.nc_de_comprobante_id] || []).push(r); } });
    return m;
  }, [rows]);

  const passEstado = (c) => estado === 'all' || String(c.resultado).toUpperCase() === estado;

  const abrirPdf = async (c) => {
    try {
      const res = await api.get(`/arca/facturacion/comprobantes/${c.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error('No se pudo generar el PDF'); }
  };

  const facturas = rows.filter((r) => FACTURA_TIPOS.has(Number(r.cbte_tipo))).filter(passEstado);
  const notas = rows.filter((r) => esNcTipo(r.cbte_tipo)).filter(passEstado);
  const facturaIds = new Set(rows.filter((r) => FACTURA_TIPOS.has(Number(r.cbte_tipo))).map((r) => r.id));
  const notasHuerfanas = notas.filter((n) => !n.nc_de_comprobante_id || !facturaIds.has(n.nc_de_comprobante_id));

  const CompInfo = ({ c, nested }) => (
    <div className={`flex items-center gap-2 min-w-0 cursor-pointer ${nested ? 'pl-6' : ''}`} onClick={() => setDetalle(c)} data-testid={`fact-comp-${c.id}`}>
      {nested && <CornerDownRight size={15} className="text-indigo-400 shrink-0" />}
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-800 truncate">{c.razon_social}</span>
          <Badge variant="outline" className={`text-[11px] ${esNcTipo(c.cbte_tipo) ? 'border-indigo-300 text-indigo-700' : ''}`}>{cbteLabel(c.cbte_tipo)}</Badge>
        </div>
        <span className="text-sm text-slate-500 font-mono">{cbteNroFmt(c)}</span>
      </div>
    </div>
  );

  const FacturaBlock = ({ c }) => {
    const ncs = ncByFact[c.id] || [];
    const autorizada = String(c.resultado).toUpperCase() === 'A';
    const hasNc = ncs.length > 0;
    const isOpen = !!expanded[c.id];
    return (
      <div className="border-b last:border-0" data-testid={`fact-comp-wrap-${c.id}`}>
        <div className="px-4 py-3 flex items-center justify-between gap-4 hover:bg-slate-50">
          <div className={`flex items-center gap-2 min-w-0 flex-1 ${hasNc ? 'cursor-pointer' : 'cursor-pointer'}`}
            onClick={() => (hasNc ? setExpanded((p) => ({ ...p, [c.id]: !p[c.id] })) : setDetalle(c))}
            data-testid={`fact-comp-${c.id}`}>
            {hasNc ? (isOpen ? <ChevronDown size={16} className="text-indigo-500 shrink-0" /> : <ChevronRight size={16} className="text-indigo-500 shrink-0" data-testid={`fact-expand-${c.id}`} />) : <span className="w-4 shrink-0" />}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-slate-800 truncate">{c.razon_social}</span>
                <Badge variant="outline" className="text-[11px]">{cbteLabel(c.cbte_tipo)}</Badge>
              </div>
              <span className="text-sm text-slate-500 font-mono">{cbteNroFmt(c)}</span>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-sm text-slate-600 font-mono">{money(c.imp_total)}</span>
            <EstadoBadge c={c} />
            {hasNc && <Badge variant="outline" className="border-indigo-400 text-indigo-700" style={{ backgroundColor: '#eef2ff' }} data-testid={`fact-tienenc-${c.id}`}>Tiene N.C.{ncs.length > 1 ? ` (${ncs.length})` : ''}</Badge>}
            {autorizada && (
              <Button size="sm" variant="outline" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50" onClick={() => onNotaCredito(c)} data-testid={`fact-nc-btn-${c.id}`}>
                <FileMinus size={14} className="mr-1" /> N. Crédito
              </Button>
            )}
            {autorizada && (
              <Button size="sm" variant="outline" onClick={() => abrirPdf(c)} data-testid={`fact-pdf-${c.id}`}>
                <FileDown size={14} className="mr-1" /> PDF
              </Button>
            )}
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDetalle(c)} data-testid={`fact-comp-detalle-btn-${c.id}`}><Info size={15} className="text-slate-400" /></Button>
            <span className="text-xs text-slate-400 hidden md:inline">{fmt(c.at)}</span>
          </div>
        </div>
        {hasNc && isOpen && ncs.map((n) => (
          <div key={n.id} className="px-4 py-2.5 flex items-center justify-between gap-4 bg-indigo-50/40 border-t border-indigo-100" data-testid={`fact-nc-row-${n.id}`}>
            <div className="flex items-center gap-2 min-w-0 pl-6 cursor-pointer" onClick={() => setDetalle(n)} data-testid={`fact-comp-${n.id}`}>
              <CornerDownRight size={15} className="text-indigo-400 shrink-0" />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-800 truncate">{n.razon_social}</span>
                  <Badge variant="outline" className="text-[11px] border-indigo-300 text-indigo-700">{cbteLabel(n.cbte_tipo)}</Badge>
                </div>
                <span className="text-sm text-slate-500 font-mono">{cbteNroFmt(n)}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-sm text-slate-600 font-mono">{money(n.imp_total)}</span>
              <EstadoBadge c={n} />
              {String(n.resultado).toUpperCase() === 'A' && (
                <Button size="sm" variant="outline" className="h-7" onClick={() => abrirPdf(n)} data-testid={`fact-pdf-${n.id}`}><FileDown size={13} className="mr-1" /> PDF</Button>
              )}
              <span className="text-xs text-slate-400 hidden md:inline">{fmt(n.at)}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const NotaFlat = ({ c }) => {
    const fact = c.nc_de_comprobante_id ? byId[c.nc_de_comprobante_id] : null;
    return (
      <div className="px-4 py-3 flex items-center justify-between gap-4 hover:bg-slate-50 border-b last:border-0" data-testid={`fact-comp-wrap-${c.id}`}>
        <div>
          <CompInfo c={c} />
          {fact && <p className="text-xs text-indigo-500 pl-0 mt-0.5">de {cbteLabel(fact.cbte_tipo)} {cbteNroFmt(fact)}</p>}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-sm text-slate-600 font-mono">{money(c.imp_total)}</span>
          <EstadoBadge c={c} />
          <span className="text-xs text-slate-400 hidden md:inline">{fmt(c.at)}</span>
        </div>
      </div>
    );
  };

  const nada = (tipoF === 'notas' ? notas.length === 0 : facturas.length === 0 && notasHuerfanas.length === 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={filtro} onValueChange={setFiltro}>
            <SelectTrigger className="w-56" data-testid="fact-comp-filtro"><SelectValue placeholder="Empresa" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las empresas</SelectItem>
              {emisores.map((e) => <SelectItem key={e.id} value={e.id}>{e.razon_social}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={estado} onValueChange={setEstado}>
            <SelectTrigger className="w-44" data-testid="fact-comp-estado"><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="A">Autorizadas</SelectItem>
              <SelectItem value="R">Rechazadas</SelectItem>
            </SelectContent>
          </Select>
          <Select value={tipoF} onValueChange={setTipoF}>
            <SelectTrigger className="w-52" data-testid="fact-comp-tipo"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Facturas y N. Crédito</SelectItem>
              <SelectItem value="facturas">Sólo facturas</SelectItem>
              <SelectItem value="notas">Sólo notas de crédito</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw size={15} className="mr-1" /> Actualizar</Button>
      </div>

      <Card className="p-0 overflow-hidden">
        {loading ? <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
          : nada ? <div className="p-8 text-center text-slate-400 text-sm">No hay comprobantes con estos filtros.</div>
          : tipoF === 'notas' ? (
            <div>{notas.map((n) => <NotaFlat key={n.id} c={n} />)}</div>
          ) : (
            <div>
              {facturas.map((c) => <FacturaBlock key={c.id} c={c} />)}
              {tipoF === 'all' && notasHuerfanas.map((n) => <NotaFlat key={n.id} c={n} />)}
            </div>
          )}
      </Card>

      <Dialog open={!!detalle} onOpenChange={(o) => !o && setDetalle(null)}>
        <DialogContent data-testid="fact-comp-detalle">
          <DialogHeader><DialogTitle>{cbteLabel(detalle?.cbte_tipo)} {detalle ? cbteNroFmt(detalle) : ''}</DialogTitle><DialogDescription>Detalle del comprobante electrónico.</DialogDescription></DialogHeader>
          {detalle && (
            <div>
              <Row label="Empresa" value={`${detalle.razon_social} (${detalle.cuit_emisor})`} />
              <Row label="Ambiente" value={detalle.environment} />
              <Row label="Resultado" value={detalle.resultado} />
              <Row label="CAE" value={detalle.cae} />
              <Row label="Vto CAE" value={detalle.cae_vto} />
              <Row label="Receptor" value={`${detalle.doc_tipo} · ${detalle.doc_nro}`} />
              <Row label="Neto" value={money(detalle.imp_neto)} />
              <Row label="IVA" value={money(detalle.imp_iva)} />
              <Row label="Total" value={money(detalle.imp_total)} />
              {detalle.descripcion && <Row label="Descripción" value={detalle.descripcion} />}
              {detalle.nc_de_comprobante_id && byId[detalle.nc_de_comprobante_id] && (
                <Row label="Asociada a" value={`${cbteLabel(byId[detalle.nc_de_comprobante_id].cbte_tipo)} ${cbteNroFmt(byId[detalle.nc_de_comprobante_id])}`} />
              )}
              {(ncByFact[detalle.id] || []).length > 0 && (
                <Row label="Notas de crédito" value={(ncByFact[detalle.id] || []).map((n) => cbteNroFmt(n)).join(', ')} />
              )}
              <Row label="Fecha" value={fmt(detalle.at)} />
              <Row label="Usuario" value={detalle.usuario} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ======================= Página =======================
export default function ArcaFacturacion() {
  const [emisores, setEmisores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tab, setTab] = useState('empresas');
  const [emitPreset, setEmitPreset] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/arca/facturacion/emisores');
      setEmisores(res.data || []);
    } catch (e) {
      if (e.response?.status !== 403) toast.error(e.response?.data?.detail || 'Error cargando empresas');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const goNotaCredito = (factura) => {
    setEmitPreset({
      nc_de_comprobante_id: factura.id,
      emisor_id: factura.emisor_id,
      pto_vta: String(factura.pto_vta),
      cbte_tipo: String(NC_DE[Number(factura.cbte_tipo)] || factura.cbte_tipo),
      doc_tipo: String(factura.doc_tipo),
      doc_nro: String(factura.doc_nro ?? ''),
      condicion_iva_receptor_id: String(factura.condicion_iva_receptor_id || '5'),
      iva_alicuota: String(factura.iva_alicuota || '21'),
      imp_total: factura.imp_total != null ? String(factura.imp_total) : '',
      origen: { tipo: factura.cbte_tipo, pto_vta: factura.pto_vta, nro: factura.cbte_nro, cbte_fch: factura.cbte_fch, label: `${cbteLabel(factura.cbte_tipo)} ${cbteNroFmt(factura)}` },
    });
    setTab('emitir');
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="arca-facturacion-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Banknote size={22} /> Facturación Electrónica</h1>
        <p className="text-slate-500 text-sm mt-1">Emití y consultá facturas electrónicas (WSFEv1) de las empresas que administrás. Cada empresa usa su propio certificado.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="fact-tabs">
          <TabsTrigger value="empresas" data-testid="fact-tab-empresas">Empresas</TabsTrigger>
          <TabsTrigger value="emitir" data-testid="fact-tab-emitir">Emitir</TabsTrigger>
          <TabsTrigger value="comprobantes" data-testid="fact-tab-comprobantes">Facturas generadas</TabsTrigger>
          <TabsTrigger value="lotes" data-testid="fact-tab-lotes">Lotes</TabsTrigger>
        </TabsList>

        <TabsContent value="empresas" className="mt-4">
          {loading ? <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
            : <EmpresasTab emisores={emisores} reload={reload} />}
        </TabsContent>
        <TabsContent value="emitir" className="mt-4">
          <EmitirTab emisores={emisores} preset={emitPreset} clearPreset={() => setEmitPreset(null)} onEmitido={() => setRefreshKey((k) => k + 1)} />
        </TabsContent>
        <TabsContent value="comprobantes" className="mt-4">
          <ComprobantesTab emisores={emisores} refreshKey={refreshKey} onNotaCredito={goNotaCredito} />
        </TabsContent>
        <TabsContent value="lotes" className="mt-4">
          <LotesTab emisores={emisores} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
